import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  FileText,
  Loader2,
  Download,
  Upload,
  Trash2,
  LogIn,
  RefreshCw,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { useSortableData } from "@/hooks/useSortableData";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime } from "@/lib/dateFormat";
import { ftpApi } from "@/services/api/ftp";
import type { AuditEvent, FtpUser } from "@/services/api/ftp";

// SFTPGo webhook event types — the only values that can appear in `action`.
// Mirrors the `actions.execute_on` list in src/ftp/sftpgo.json on the backend.
const ACTION_OPTIONS = [
  { value: "upload", label: "Upload" },
  { value: "download", label: "Download" },
  { value: "delete", label: "Delete" },
  { value: "rename", label: "Rename" },
  { value: "mkdir", label: "Make directory" },
  { value: "rmdir", label: "Remove directory" },
  { value: "login", label: "Login" },
] as const;

// Date-range presets. "custom" reveals the two manual date inputs below.
// Empty string ("all") means no filter.
const RANGE_PRESETS = [
  { value: "all", label: "All time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "custom", label: "Custom range…" },
] as const;

type RangePreset = (typeof RANGE_PRESETS)[number]["value"];

/** Map a preset key to a {start_date, end_date} pair in YYYY-MM-DD. */
function resolvePresetRange(preset: RangePreset): { start: string; end: string } {
  if (preset === "all" || preset === "custom") return { start: "", end: "" };
  const days =
    preset === "24h" ? 1 : preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

// Sentinel used by Select; translates to "no filter" when sent to API.
const ALL = "__all";

export const Route = createFileRoute("/portal/ftp/audit")({
  component: FtpAuditPage,
});

function FtpAuditPage() {
  const { token } = useAuth();

  const [usernameFilter, setUsernameFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [rangePreset, setRangePreset] = useState<RangePreset>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orgUsers, setOrgUsers] = useState<FtpUser[]>([]);

  // Fetch the org's SFTP users once for the username dropdown.
  // Cheap (max 50 per enterprise tier); single shot on mount.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await ftpApi.listUsers(token, "limit=500&offset=0");
        if (!cancelled) setOrgUsers(response.users ?? []);
      } catch {
        if (!cancelled) setOrgUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // When the preset changes, recompute the date bounds. "custom" leaves the
  // existing values in place so the user can edit them in the revealed inputs.
  function handleRangeChange(next: RangePreset) {
    setRangePreset(next);
    if (next !== "custom") {
      const { start, end } = resolvePresetRange(next);
      setStartDate(start);
      setEndDate(end);
    }
  }

  const fetchAudit = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<AuditEvent>> => {
      if (!token)
        return { data: [], totalCount: 0, page, pageSize };

      const params = new URLSearchParams();
      params.set("limit", String(pageSize));
      params.set("offset", String((page - 1) * pageSize));
      if (usernameFilter) params.set("username", usernameFilter);
      if (actionFilter) params.set("action", actionFilter);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      const response = await ftpApi.getAuditLog(token, params.toString());
      return {
        data: response.events || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, usernameFilter, actionFilter, startDate, endDate],
  );

  const {
    data: events,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    refresh,
  } = usePaginatedData<AuditEvent>({
    fetchFn: fetchAudit,
    initialPageSize: 50,
    dependencies: [token, usernameFilter, actionFilter, startDate, endDate],
  });

  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      refresh();
      toast.success("Audit log refreshed");
    } finally {
      // Give the fetch a beat to spin so the user sees feedback.
      setTimeout(() => setRefreshing(false), 400);
    }
  }

  // Client-side sorting on current page data
  const { sortedData, requestSort, getSortDirection } = useSortableData(events, {
    initialSort: { key: "created_at", direction: "desc" },
  });

  function formatDate(dateStr: string): string {
    if (!dateStr) return "-";
    return formatDateTime(dateStr);
  }

  function formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return "-";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  function getActionIcon(action: string) {
    // Guard against the API returning null/undefined for any reason —
    // a thrown .toLowerCase() crashes the whole row's render.
    switch ((action ?? "").toLowerCase()) {
      case "upload":
        return <Upload className="h-3.5 w-3.5" />;
      case "download":
        return <Download className="h-3.5 w-3.5" />;
      case "delete":
        return <Trash2 className="h-3.5 w-3.5" />;
      case "login":
        return <LogIn className="h-3.5 w-3.5" />;
      default:
        return <FileText className="h-3.5 w-3.5" />;
    }
  }

  function getActionVariant(
    action: string,
  ): "default" | "secondary" | "destructive" | "outline" | "success" {
    // NOTE: deliberately NOT using "info" here. The theme's --accent
    // CSS var is declared as a hex colour (#06b6d4) but Tailwind's
    // bg-accent/20 expects space-separated HSL — so info-variant
    // badges render with a transparent background AND foreground,
    // making them appear empty. Until that theme bug is fixed
    // separately, every audit-action badge uses one of the variants
    // backed by proper HSL declarations.
    switch ((action ?? "").toLowerCase()) {
      case "upload":
        return "success";
      case "download":
        return "default";
      case "delete":
        return "destructive";
      default:
        return "secondary";
    }
  }

  function clearFilters() {
    setUsernameFilter("");
    setActionFilter("");
    setRangePreset("all");
    setStartDate("");
    setEndDate("");
  }

  const hasFilters =
    usernameFilter || actionFilter || rangePreset !== "all" || startDate || endDate;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Audit Log</h1>
          <p className="mt-2 text-lg text-slate-600">
            File transfer activity and events for your organisation
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing || isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filters</CardTitle>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="username-filter">Username</Label>
              <Select
                value={usernameFilter || ALL}
                onValueChange={(v) => setUsernameFilter(v === ALL ? "" : v)}
              >
                <SelectTrigger id="username-filter">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All users</SelectItem>
                  {orgUsers.map((u) => (
                    <SelectItem key={u.username} value={u.username}>
                      {u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="action-filter">Action</Label>
              <Select
                value={actionFilter || ALL}
                onValueChange={(v) => setActionFilter(v === ALL ? "" : v)}
              >
                <SelectTrigger id="action-filter">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All actions</SelectItem>
                  {ACTION_OPTIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="range-preset">Date range</Label>
              <Select
                value={rangePreset}
                onValueChange={(v) => handleRangeChange(v as RangePreset)}
              >
                <SelectTrigger id="range-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_PRESETS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {rangePreset === "custom" && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Events table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Events
          </CardTitle>
          <CardDescription>
            Showing file transfer and access events
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="mb-4 h-12 w-12 text-muted-foreground/30" />
              <p className="text-muted-foreground">
                {hasFilters
                  ? "No events match your filters"
                  : "No audit events yet"}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      direction={getSortDirection("created_at")}
                      onSort={() => requestSort("created_at")}
                    >
                      Timestamp
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("username")}
                      onSort={() => requestSort("username")}
                    >
                      Username
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("action")}
                      onSort={() => requestSort("action")}
                    >
                      Action
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("file_path")}
                      onSort={() => requestSort("file_path")}
                    >
                      File Path
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("file_size")}
                      onSort={() => requestSort("file_size")}
                    >
                      Size
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("protocol")}
                      onSort={() => requestSort("protocol")}
                    >
                      Via
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("ip_address")}
                      onSort={() => requestSort("ip_address")}
                    >
                      IP Address
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("status")}
                      onSort={() => requestSort("status")}
                    >
                      Status
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(event.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {event.username}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getActionVariant(event.action)}
                          className="gap-1"
                        >
                          {getActionIcon(event.action)}
                          {event.action}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="max-w-[300px] truncate font-mono text-xs"
                        title={event.file_path}
                      >
                        {event.file_path || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatBytes(event.file_size)}
                      </TableCell>
                      <TableCell>
                        {/* "Portal" rows come from the portal Files tab;
                            everything else arrived through SFTPGo. */}
                        <Badge variant="outline" data-testid="audit-via">
                          {event.protocol || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {event.ip_address || "-"}
                      </TableCell>
                      <TableCell>
                        {/* Backend (audit_service.py) stores status as
                            "success" or "failed" — not "OK". The previous
                            ===" OK" comparison meant every row painted
                            destructive (red) regardless of outcome. */}
                        <Badge
                          variant={
                            event.status === "success"
                              ? "success"
                              : "destructive"
                          }
                        >
                          {event.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination.totalCount > 0 && (
                <div className="mt-4 border-t pt-4">
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    pageSizeOptions={pageSizeOptions}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    canGoPrev={canGoPrev}
                    canGoNext={canGoNext}
                    onPageChange={goToPage}
                    onPageSizeChange={setPageSize}
                    onFirstPage={firstPage}
                    onLastPage={lastPage}
                    onPrevPage={prevPage}
                    onNextPage={nextPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
